import type { ReactNode } from "react";

import { CopyButton } from "@/components/copy-button";
import { highlight, type Language } from "@/lib/highlight";
import { cn } from "@/lib/utils";

/**
 * A block of code as the site shows it: highlighted, scrollable, with a copy button.
 *
 * The one place code is rendered, so the getting-started page (from markdown) and the
 * challenge card (from strings) look the same. Highlighting is synchronous and local --
 * four grammars, no worker, no wasm -- so a block renders in one pass with no flash of
 * plain text.
 */
export function CodeBlock({
  code,
  language,
  header,
  copy,
  className,
}: {
  code: string;
  language?: Language | string;
  /** Rendered above the code, in place of the floating copy button. */
  header?: ReactNode;
  /** What the copy button puts on the clipboard, when it differs from `code`. */
  copy?: string;
  className?: string;
}) {
  const tokens = highlight(code, language);
  return (
    <div className={cn("bg-muted relative min-w-0 rounded-md", className)}>
      {header ? (
        <div className="flex items-center gap-3 border-b px-3 py-2">{header}</div>
      ) : (
        <div className="absolute top-1.5 right-1.5 flex items-center gap-1.5">
          {language ? (
            <span className="text-muted-foreground hidden text-[10px] font-medium uppercase sm:inline">
              {language}
            </span>
          ) : null}
          <CopyButton text={copy ?? code} className="bg-background" />
        </div>
      )}
      <pre className="hljs overflow-x-auto p-3 text-xs leading-relaxed">
        <code>{tokens}</code>
      </pre>
    </div>
  );
}
